import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  MapPin, 
  Users, 
  Clock, 
  ChevronRight, 
  AlertCircle,
  CheckCircle2,
  Calendar,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { driverApi } from "@/lib/api";
import { useSocket } from "@/hooks/useSocket";
import { cn } from "@/lib/utils";

import { AppHeader, BottomNav } from "@/components/app-header";

interface Ride {
  id: string;
  status: string;
  pickup: any;
  destination: any;
  estimated_price: number;
  offer_price: number;
  seats_needed: number;
  created_at: string;
  passenger?: {
    full_name: string;
    rating: number;
    profile_photo?: string;
  };
}

const formatAddress = (val: any): string => {
  if (!val) return "Unknown location";
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    if (val.address) return formatAddress(val.address);
    if (val.name) return val.name;
  }
  return "Unknown location";
};

type TabType = "requested" | "upcoming" | "completed";

export default function DriverTrips() {
  const navigate = useNavigate();
  const { on } = useSocket();
  const [activeTab, setActiveTab] = useState<TabType>("requested");
  const [trips, setTrips] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTrips = async (tab: TabType) => {
    setLoading(true);
    try {
      // Map tab to backend status
      let statusParam = "";
      if (tab === "requested") statusParam = "pending";
      else if (tab === "upcoming") statusParam = "upcoming";
      else if (tab === "completed") statusParam = "completed";

      const data = await driverApi.getTrips(statusParam) as { trips: Ride[] };
      setTrips(data.trips || []);
    } catch (error: any) {
      toast.error("Failed to load trips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips(activeTab);

    if (activeTab === "requested") {
      on("RIDE_REQUESTED", () => {
        toast.info("New ride request!", { position: "top-center" });
        fetchTrips("requested");
      });
    }
  }, [activeTab]);

  const handleAccept = async (rideId: string) => {
    try {
      await driverApi.acceptRide(rideId);
      toast.success("Ride accepted!");
      // Move to upcoming
      setActiveTab("upcoming");
    } catch (error: any) {
      toast.error(error.message || "Failed to accept ride");
    }
  };

  const tabs = [
    { id: "requested", label: "Requested", icon: BellIcon },
    { id: "upcoming", label: "Upcoming", icon: Calendar },
    { id: "completed", label: "History", icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-gray-50/50 pb-24">
      <AppHeader title="Trips" />
      
      <div className="max-w-2xl mx-auto p-4">
        {/* Custom Tab Switcher */}
        <div className="flex bg-white p-1 rounded-xl border border-gray-100 shadow-sm mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all",
                activeTab === tab.id 
                  ? "bg-yenko-blue text-white shadow-md shadow-blue-500/20" 
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
              )}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="space-y-4">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-44 bg-gray-100/50 rounded-3xl animate-pulse border-2 border-gray-50" />
              ))}
            </div>
          ) : trips.length > 0 ? (
            trips.map((trip, index) => (
              <TripCard 
                key={trip.id} 
                trip={trip} 
                index={index} 
                onAccept={() => handleAccept(trip.id)}
                onClick={() => activeTab !== "requested" && navigate(`/driver/trip/${trip.id}`)}
                showActions={activeTab === "requested"}
              />
            ))
          ) : (
            <div className="text-center py-20 bg-white/40 backdrop-blur-sm rounded-3xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-gray-300" />
              </div>
              <p className="text-gray-500 font-bold">No trips found in this category</p>
              <p className="text-sm text-gray-400">Pull to refresh or check back later</p>
            </div>
          )}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out forwards;
        }
      `}} />
    </div>
  );
}

function BellIcon(props: any) {
  return (
    <svg 
      {...props} 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

function TripCard({ trip, index, onAccept, onClick, showActions }: { 
  trip: Ride, 
  index: number, 
  onAccept: () => void,
  onClick: () => void,
  showActions: boolean
}) {
  return (
    <div 
      onClick={onClick}
      style={{ animationDelay: `${index * 100}ms` }}
      className={cn(
        "bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 group overflow-hidden animate-fade-in-up opacity-0",
        !showActions && "cursor-pointer active:scale-[0.98]"
      )}
    >
      {/* User Info Header */}
      <div className="p-4 flex justify-between items-center bg-gray-50/50 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yenko-blue rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
            {trip.passenger?.full_name?.charAt(0) || "P"}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900">{trip.passenger?.full_name || "Passenger"}</h3>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-yellow-400/10 rounded-full">
                <span className="text-yellow-500 text-xs">★</span>
                <span className="text-xs font-black text-yellow-600">{(trip.passenger?.rating || 5.0).toFixed(1)}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400 font-medium mt-0.5">
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" /> {trip.seats_needed || 1} seats
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Just now
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-yenko-blue">
            ₵{(trip.offer_price || trip.estimated_price || 0).toFixed(0)}
          </div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Offered</p>
        </div>
      </div>

    <div className="p-4 space-y-4">
        <div className="flex gap-3">
          <div className="flex flex-col items-center pt-1">
            <div className="w-2 h-2 rounded-full bg-yenko-blue" />
            <div className="w-px h-6 bg-gray-200 my-1" />
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Pickup</p>
              <p className="text-sm font-medium text-gray-900 line-clamp-1">{formatAddress(trip.pickup)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Destination</p>
              <p className="text-sm font-medium text-gray-900 line-clamp-1">{formatAddress(trip.destination)}</p>
            </div>
          </div>
        </div>

        {showActions ? (
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1 rounded-xl h-11 font-bold text-gray-500 border-gray-100"
            >
              Ignore
            </Button>
            <Button 
              onClick={(e) => { e.stopPropagation(); onAccept(); }}
              className="flex-1 bg-yenko-blue text-white rounded-xl h-11 font-bold shadow-sm hover:bg-blue-600"
            >
              Accept Ride
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between pt-2">
            <div className={cn(
              "px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5",
              trip.status === "completed" ? "bg-green-50 text-green-600" : "bg-blue-50 text-blue-600"
            )}>
              <span className={cn("w-2 h-2 rounded-full", trip.status === "completed" ? "bg-green-500" : "bg-blue-500 animate-pulse")} />
              {trip.status.replace("_", " ")}
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-yenko-blue group-hover:translate-x-1 transition-all" />
          </div>
        )}
      </div>
    </div>
  );
}
